import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthApi } from '../api/Auth';

interface User {
    id: string;
    email: string;
    name: string;
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<void>;
    register: (email: string, password: string, name: string) => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        // Kiểm tra xem có user đã đăng nhập trong sessionStorage không
        const storedUser = sessionStorage.getItem('auth_user');
        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
            } catch (error) {
                console.error('Error parsing stored user:', error);
                sessionStorage.removeItem('auth_user');
            }
        }
        setIsLoading(false);
    }, []);

    const login = async (email: string, password: string) => {
        const response = await AuthApi.Login({ email, password });
        if (response.success) {
            setUser(response.data.user);
            sessionStorage.setItem('auth_user', JSON.stringify(response.data.user));
            sessionStorage.setItem('auth_token', response.data.token);
        }
    };

    const register = async (email: string, password: string, name: string) => {
        const response = await AuthApi.Register({ email, password, name });
        if (response.success) {
            setUser(response.data.user);
            sessionStorage.setItem('auth_user', JSON.stringify(response.data.user));
            sessionStorage.setItem('auth_token', response.data.token);
        }
    };

    const logout = () => {
        setUser(null);
        sessionStorage.removeItem('auth_user');
        sessionStorage.removeItem('auth_token');
    };

    const value: AuthContextType = {
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

